import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useNavigate } from "react-router";

import type { Me } from "../api/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/DropdownMenu";
import { initials } from "../lib/initials";
import type { ThemePreference } from "../theme/theme";
import { useTheme } from "../theme/theme-context";
import { useGoToMicrosoft } from "./goToMicrosoft";
import { useOwnerToolsOpener } from "./owner-tools-context";
import { ME_QUERY_KEY, useSession } from "./session-context";

const ROLE_LABEL = { owner: "Owner", tech: "Technician" } as const;

function isThemePreference(value: string): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

/**
 * The avatar and its menu: who you are, My settings (ADR 0200), the theme
 * choice, Owner tools for an owner (as the backtick key opens them), and sign
 * out.
 */
export function UserMenu() {
  const { me, auth, api } = useSession();
  const navigate = useNavigate();
  const openOwnerTools = useOwnerToolsOpener();
  const trigger = useRef<HTMLButtonElement>(null);
  const { preference, setPreference } = useTheme();
  const queryClient = useQueryClient();
  const signOut = useGoToMicrosoft();
  // One save at a time, in order, so the last choice is the one saved.
  const saveTheme = useMutation({
    mutationFn: (theme: ThemePreference) => api.setTheme(theme),
    scope: { id: "theme" },
  });

  /**
   * Applies at once and is kept in this browser, then saved for the user's
   * other devices (ADR 0041). The cached user shows the choice straight away,
   * so a slower answer to an earlier save can't bring the old one back; nor
   * can a read of the user already under way, which is called off first
   * (#863). If the save fails, the choice still applies here.
   */
  function chooseTheme(theme: ThemePreference) {
    setPreference(theme);
    void queryClient.cancelQueries({ queryKey: ME_QUERY_KEY });
    queryClient.setQueryData<Me>(ME_QUERY_KEY, (old) => (old ? { ...old, theme } : old));
    saveTheme.mutate(theme);
  }
  const name = me.display_name ?? me.email ?? "Signed in";
  const role = ROLE_LABEL[me.role];
  const avatar = initials(me.display_name, me.email);

  return (
    <div className="relative">
      {/* Non-modal: a header menu need not lock page scrolling, and the scroll
          lock injects an inline <style> that the Content Security Policy
          forbids. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          ref={trigger}
          // The initials it shows come first, so its name holds what's on it (WCAG 2.5.3).
          aria-label={`${avatar}, ${name}, ${role}. Open user menu`}
          className="flex size-[38px] cursor-pointer items-center justify-center rounded-pill bg-brand-tint text-[13px] font-bold text-brand-ink-on-tint"
        >
          {avatar}
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <div className="px-3 py-2">
            <p className="font-semibold">{name}</p>
            {me.email && me.email !== name ? <p className="text-sm text-muted">{me.email}</p> : null}
            <p className="mt-1 text-sm text-muted">{role}</p>
          </div>
          <DropdownMenuSeparator />
          {/* Your own settings alone; the app's are on the owners' tabs (ADR 0200). */}
          <DropdownMenuItem
            onSelect={() => {
              void navigate("/my-settings");
            }}
          >
            My settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={preference}
            onValueChange={(value) => {
              if (isThemePreference(value)) {
                chooseTheme(value);
              }
            }}
          >
            <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          {openOwnerTools ? (
            <>
              <DropdownMenuSeparator />
              {/* On a phone, a backtick is hard to type with no keyboard open. */}
              <DropdownMenuItem
                onSelect={() => {
                  openOwnerTools(trigger.current);
                }}
              >
                Owner tools
              </DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              signOut.go(() => auth.signOut());
            }}
          >
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Why signing out didn't start, as Privacy mode's note (#865). */}
      {signOut.failed === null ? null : (
        <p
          role="alert"
          className="absolute top-full right-0 z-10 mt-1.5 w-64 rounded-row border border-line bg-panel px-3 py-2 text-xs text-status-needs-client-fg shadow-sm"
        >
          {signOut.failed}
        </p>
      )}
    </div>
  );
}
